import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import HistoryIcon from "@mui/icons-material/History";
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, CircularProgress } from '@mui/material';
import { forwardRef, useContext, useImperativeHandle, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ControlledTextField } from 'src/components/forms';
import { useLoadOptions } from 'src/hooks/useLoadOptions';
import CardActions from 'src/pages/sistema/components/CardActions';
import { EntityWithIdFields } from 'src/types';

import { HttpLCSLHPTI } from '../api/httpLCSLSHPTI';
import {
  LCSLHPTIGroupedHeader,
  LCSLHPTIRow,
  pivotLCSLHPTIData,
  useLCSLHPTITablaValor
} from '../hooks/useLCSLHPTITablaValor';
import { FormUpdateLCSLHPTITablaValores, schemaFormUpdateLCSLHPTITablaValores } from '../types/dto';
import { OperacionLCSLHPTIFields, OperacionLCSLHPTITablaValorFields } from '../types/fields';

import LCLSHPTITablaValores from './LCLSHPTITablaValores';
import LCSLHPTITablaValoresEditorGrid from './LCSLHPTITablaValoresEditorGrid';

import { useSubmitOperacion } from 'src/pages/sistema/hooks/useSubmitOperacion';
import { ContextOperacion, ContextOperacionType } from 'src/pages/sistema/RefactorLayout';
import { PermissionType } from 'src/types/security';
import {
  useLCSLHPTIConcentracionTipos,
  useLCSLHPTILimiteAgregadoTipos,
  useLCSLHPTILimiteHoldingTipos,
  useLCSLHPTILimitePerdidaTipos,
  useLCSLHPTILimiteTipos
} from '../hooks/useLCSLHPTICache';
import { LCSLHPTITablaValoresHistorialDialog } from './LCSLHPTITablaValoresHistorialDialog';
import { OperacionLCSLHPTI } from '../types/types';

interface SeccionLCSLHPTITablaValoresProps {
  operacion: OperacionLCSLHPTI;
  codSistemaPrincipal: number;
  refetchOperacion?: () => void;
}

export interface SeccionLCSLHPTITablaValoresHandle {
  hasCambiosSinGuardar: () => boolean;
  guardarCambios: () => Promise<boolean>;
}

const SeccionLCSLHPTITablaValores = forwardRef<SeccionLCSLHPTITablaValoresHandle, SeccionLCSLHPTITablaValoresProps>(({ operacion, codSistemaPrincipal, refetchOperacion }, ref) => {
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);
  const { permisoTipo } = useContext<ContextOperacionType>(ContextOperacion)

  const [isEditing, setIsEditing] = useState(false);
  const { submitOperacion } = useSubmitOperacion();

  const {
    valores: celdasOriginales,
    groupedHeaders: headersOriginales,
    rows: rowsOriginales,
    isLoading: isLoadingTabla,
    refetch
  } = useLCSLHPTITablaValor(operacion[EntityWithIdFields.Id]);

  const [editPivot, setEditPivot] = useState<{ headers: LCSLHPTIGroupedHeader[], rows: LCSLHPTIRow[] }>({
    headers: [],
    rows: []
  });

  const methods = useForm<FormUpdateLCSLHPTITablaValores>({
    resolver: zodResolver(schemaFormUpdateLCSLHPTITablaValores),
    defaultValues: {
      [OperacionLCSLHPTIFields.Observaciones]: '',
      [OperacionLCSLHPTIFields.TablaValores]: [],
      [OperacionLCSLHPTIFields.LimiteAgregadoPosicionComprada]: operacion[OperacionLCSLHPTIFields.LimiteAgregadoPosicionComprada] || '',
      [OperacionLCSLHPTIFields.LimiteAgregadoPosicionVendida]: operacion[OperacionLCSLHPTIFields.LimiteAgregadoPosicionVendida] || ''
    }
  });

  const { limiteTipos, isLoading: isLoadingLimite, isError: isErrorLimite, error: errorLimite } = useLCSLHPTILimiteTipos();
  const { limiteHoldingTipos, isLoading: isLoadingHolding, isError: isErrorHolding, error: errorHolding } = useLCSLHPTILimiteHoldingTipos();
  const { concentracionTipos, isLoading: isLoadingConcentracion, isError: isErrorConcentracion, error: errorConcentracion } = useLCSLHPTIConcentracionTipos();
  const { limitePerdidaTipos, isLoading: isLoadingPerdida, isError: isErrorPerdida, error: errorPerdida } = useLCSLHPTILimitePerdidaTipos();
  const { limiteAgregadoTipos, isLoading: isLoadingLimiteAgregado, isError: isErrorLimiteAgregado, error: errorLimiteAgregado } = useLCSLHPTILimiteAgregadoTipos();

  const selectoresMapping = {
    limite_credito: useLoadOptions({ data: limiteTipos, loading: isLoadingLimite, isError: isErrorLimite, error: errorLimite }),
    limite_holding: useLoadOptions({ data: limiteHoldingTipos, loading: isLoadingHolding, isError: isErrorHolding, error: errorHolding }),
    concentracion: useLoadOptions({ data: concentracionTipos, loading: isLoadingConcentracion, isError: isErrorConcentracion, error: errorConcentracion }),
    limite_perdida: useLoadOptions({ data: limitePerdidaTipos, loading: isLoadingPerdida, isError: isErrorPerdida, error: errorPerdida }),
    limite_agregado: useLoadOptions({ data: limiteAgregadoTipos, loading: isLoadingLimiteAgregado, isError: isErrorLimiteAgregado, error: errorLimiteAgregado }),
  };

  const handleEdit = () => {
    methods.reset({
      [OperacionLCSLHPTIFields.Observaciones]: '',
      [OperacionLCSLHPTIFields.TablaValores]: celdasOriginales as any,
      [OperacionLCSLHPTIFields.LimiteAgregadoPosicionComprada]: operacion[OperacionLCSLHPTIFields.LimiteAgregadoPosicionComprada] || '',
      [OperacionLCSLHPTIFields.LimiteAgregadoPosicionVendida]: operacion[OperacionLCSLHPTIFields.LimiteAgregadoPosicionVendida] || ''
    });
    setEditPivot({ headers: headersOriginales, rows: rowsOriginales });
    setIsEditing(true);
  };

  const actualizarPivotLocal = (nuevosValores: any[]) => {
    const p = pivotLCSLHPTIData(nuevosValores);
    setEditPivot({ headers: p.groupedHeaders, rows: p.rows });
  };

  const handleAgregarOtros = () => {
    const codOtros = 15;
    const nuevoRowId = `temp-${Date.now()}`;
    const valoresActuales = (methods.getValues(OperacionLCSLHPTIFields.TablaValores) || []) as any[];

    const nuevasCeldas = editPivot.headers.flatMap(group =>
      group.columnas.map(col => {
        const celdaReferencia = valoresActuales.find(v => v[OperacionLCSLHPTITablaValorFields.CodOperacionTipo] === col.codOperacionTipo);

        return {
          [OperacionLCSLHPTITablaValorFields.CodInstrumento]: codOtros,
          [OperacionLCSLHPTITablaValorFields.DescInstrumento]: "Otros",
          [OperacionLCSLHPTITablaValorFields.CodOperacionTipo]: col.codOperacionTipo,
          [OperacionLCSLHPTITablaValorFields.DescOperacionTipo]: col.descOperacionTipo,
          [OperacionLCSLHPTITablaValorFields.Agrupacion]: group.agrupacion,
          [OperacionLCSLHPTITablaValorFields.Valor]: "",
          [OperacionLCSLHPTITablaValorFields.Orden]: 99,
          [OperacionLCSLHPTITablaValorFields.PermiteEditarInstrumento]: true,
          [OperacionLCSLHPTITablaValorFields.TipoSelector]: celdaReferencia?.[OperacionLCSLHPTITablaValorFields.TipoSelector] || "",
          rowId: nuevoRowId,
          [EntityWithIdFields.Id]: 0
        };
      })
    );

    const nuevosValores = [...valoresActuales, ...nuevasCeldas];
    methods.setValue(OperacionLCSLHPTIFields.TablaValores, nuevosValores, { shouldDirty: true });
    actualizarPivotLocal(nuevosValores);
  };

  const handleEliminarOtros = (rowKey: string | number) => {
    const actuales = (methods.getValues(OperacionLCSLHPTIFields.TablaValores) || []) as any[];
    const filtrados = actuales.filter((v: any) =>
      (v.rowId || v[OperacionLCSLHPTITablaValorFields.CodInstrumento]) !== rowKey
    );

    methods.setValue(OperacionLCSLHPTIFields.TablaValores, filtrados, { shouldDirty: true });
    actualizarPivotLocal(filtrados);
  };

  const onSave = async (data: FormUpdateLCSLHPTITablaValores): Promise<boolean> => {
    let huboExito = false;

    await submitOperacion(data, {
      apiCall: (payload) => HttpLCSLHPTI.updateTablaValores(operacion[EntityWithIdFields.Id], payload),
      successMessage: 'Matriz de límites actualizada correctamente',
      navigateTo: null,
      onSuccess: async () => {
        huboExito = true;
        await refetch();
        refetchOperacion?.();
        setIsEditing(false);
      }
    });

    return huboExito;
  };

  const handleCancel = () => {
    setIsEditing(false);
    methods.reset();
  };

  const hasCambiosSinGuardar = () => isEditing && methods.formState.isDirty;

  const guardarCambios = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      methods.handleSubmit(
        async (data) => resolve(await onSave(data)),
        () => resolve(false)
      )();
    });
  };

  useImperativeHandle(ref, () => ({
    hasCambiosSinGuardar,
    guardarCambios
  }), [isEditing, methods.formState.isDirty]);


  return (
    <CardActions
      title="Matriz de Límites y Condiciones"
      icon='mdi:table-account'
      colapsable
      tableContent
      actions={
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setHistorialDialogOpen(true)}
            sx={{ textTransform: 'none' }}
            variant={"contained"}
          >
            Historial
          </Button>

          {permisoTipo >= PermissionType.Write && (
            !isEditing ? (
              <Button
                variant="outlined"
                size="small"
                onClick={handleEdit}
                startIcon={<Icon icon="mdi:pencil" />}
              >
                Editar
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={handleCancel}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={methods.handleSubmit(onSave)}
                  startIcon={<SaveIcon />}
                >
                  Guardar Cambios
                </Button>
              </Box>
            )
          )}
        </Box>
      }
    >
      {isLoadingTabla ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>
      ) : isEditing ? (
        <FormProvider {...methods}>
          <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <ControlledTextField
              name={OperacionLCSLHPTIFields.Observaciones}
              control={methods.control}
              label="Observaciones del cambio"
              multiline
              rows={2}
            />
            <LCSLHPTITablaValoresEditorGrid
              formMethods={methods}
              groupedHeaders={editPivot.headers}
              rows={editPivot.rows}
              isLoading={false}
              selectoresMapping={selectoresMapping}
              onAgregarOtros={handleAgregarOtros}
              onEliminarOtros={handleEliminarOtros}
              permiteEditar={true}
            />
          </Box>
        </FormProvider>
      ) : (
        <LCLSHPTITablaValores headers={headersOriginales} rows={rowsOriginales} 
        limiteAgregadoPosicionComprada={operacion[OperacionLCSLHPTIFields.LimiteAgregadoPosicionComprada]}
        limiteAgregadoPosicionVendida={operacion[OperacionLCSLHPTIFields.LimiteAgregadoPosicionVendida]}
        />
      )}
      {
        historialDialogOpen && (<LCSLHPTITablaValoresHistorialDialog
          open={historialDialogOpen}
          onClose={() => setHistorialDialogOpen(false)}
          idOperacion={operacion[EntityWithIdFields.Id]} />)
      }
    </CardActions>
  );
});

export default SeccionLCSLHPTITablaValores;